import { Injectable } from "@angular/core";

import { from, Observable } from "rxjs";
import { first, map, switchMap } from "rxjs/operators";

import { DffGroupedContent, SessionStorage, trim } from "@dffedb/util";
import { Adresse } from "@e-forsyning/common/adresse";
import { BrugerRepository } from "@e-forsyning/common/bruger";
import { DffMailInternal, EforsyningMailBuilderService, EforsyningMailSenderService, MailHelper } from "@e-forsyning/common/mail";
import { EforsyningUrlService } from "@e-forsyning/common/util";

import { IFlyttemeddelelse } from "../model/flyttemeddelelse.model";
import { FlyttemeddelelseKvitteringService } from "./flyttemeddelelse-kvittering.service";

@Injectable({ providedIn: "root" })
export class FlyttemeddelelseKvitteringMailService {
    private subject = "Kvittering for flytning";
    private kvittering: DffGroupedContent;
    constructor(
        private readonly eforsyningMailBuilder: EforsyningMailBuilderService,
        private readonly mailSender: EforsyningMailSenderService,
        private readonly brugerRepository: BrugerRepository,
        private readonly urlService: EforsyningUrlService,
        private readonly kvitteringService: FlyttemeddelelseKvitteringService,
        private readonly sessionStorage: SessionStorage
    ) {}

    public async sendMail(model: IFlyttemeddelelse, mail: string): Promise<void> {
        const mail$ = await this.genererMail(model, mail);
        return this.mailSender.sendMailFraUmbraco("SendFlytningKviteringsMail", mail$);
    }

    private async genererMail(model: IFlyttemeddelelse, mail: string): Promise<DffMailInternal> {
        this.kvittering = await this.kvitteringService.genererKvittering(model).pipe(first()).toPromise();

        const minAdresse = await this.hentAdresseForAktuelleInstallation().pipe(first()).toPromise();
        this.tilfoejTilModel(minAdresse, this.kvittering);
        const mailData = JSON.stringify(this.kvittering);
        const rawCredentials = await this.sessionStorage.select("credentials").pipe(first()).toPromise();
        const allCredentials = JSON.parse(rawCredentials || null) || {};
        const nodeId = allCredentials["bruger"];

        const result: DffMailInternal = {
            sideId: nodeId, // this.installation.forsyningIndstillinger.sideId,
            to: mail,
            //subject: this.subject,
            model: mailData
        };
        return result;
    }

    private tilfoejTilModel(ekstra: string, kvittering: DffGroupedContent): DffGroupedContent {
        const addedContent: DffGroupedContent = {
            groups: [
                {
                    title: "Ekstra",
                    rows: [{ key: "adresse", value: ekstra }]
                }
            ]
        };
        kvittering.groups.unshift(addedContent.groups[0]);
        return kvittering;
    }
    private genererBody(adresse: string, kvittering: DffGroupedContent): Observable<string> {
        const mineDokumenterUrl$ = from(this.urlService.hentAbsolutUrl("mine-dokumenter"));
        return mineDokumenterUrl$.pipe(
            switchMap((mineDokumenterUrl) =>
                this.eforsyningMailBuilder.genererAsync(
                    this.subject,
                    MailHelper.div(`Tak for dine flytteoplysninger vedrørende adressen <strong>${adresse}</strong>`),
                    MailHelper.space(),
                    MailHelper.div(
                        `Bemærk at når vi registrerer din flytning, forsvinder din adgang til E|Forsyning.
                        Du kan derfor med fordel downloade vigtige dokumenter fra menupunktet 
                        <a href="${mineDokumenterUrl}">Mine dokumenter</a> til senere brug.`
                    ),
                    MailHelper.space(),
                    MailHelper.kvitteringPanel(kvittering)
                )
            )
        );
    }

    private hentAdresseForAktuelleInstallation(): Observable<string> {
        return this.brugerRepository.hentBrugerForAktuelleInstallation().pipe(map((bruger) => this.adresseToStringOneLine(bruger.adresse)));
    }

    private adresseToStringOneLine(adresse: Adresse): string {
        const vej = trim(`${adresse.vejnavn || ""} ${adresse.husnr || ""}`);
        const postnr = trim(`${adresse.postnr || ""} ${adresse.bynavn || ""}`);
        return trim(`${vej}, ${postnr}`, ", ");
    }
}
